<template>
  <b-modal :visible="visible" :title="ticket ? `Editar OS ${ticket.numero}` : 'Abrir chamado'"
    size="lg" modal-class="ast-modal" hide-footer :no-close-on-backdrop="busy"
    :no-close-on-esc="busy" :hide-header-close="busy" @hidden="$emit('close')">
    <form class="ast-form" @submit.prevent="submit">
      <p class="ast-muted">Selecione um cliente e um produto ativo da empresa. O técnico responsável é informado livremente.</p>
      <b-alert :show="!!selectionError" variant="danger">{{ selectionError }}</b-alert>
      <b-alert :show="!!error" variant="danger">{{ error }}
        <button v-if="conflict" type="button" class="btn btn-sm btn-outline-danger" :disabled="busy" @click="$emit('reload')">Recarregar e descartar alterações locais</button>
      </b-alert>
      <fieldset :disabled="busy">
        <div class="ast-grid">
          <label v-for="field in fields" :key="field.key" class="ast-field">{{ field.label }}{{ field.required ? ' *' : '' }}
            <template v-if="field.lookup">
              <v-select :value="selected[field.lookup]" :options="options[field.lookup]" label="label"
                :filterable="false" :loading="loading[field.lookup]" :disabled="busy"
                :input-id="`ast-${field.lookup}`" placeholder="Busque por nome ou código"
                @input="choose(field, $event)" @search="searchOptions(field.lookup, $event)">
                <template #no-options>Nenhum resultado. Digite um nome ou código para buscar.</template>
              </v-select>
              <small v-if="more[field.lookup]" class="ast-muted">Mostrando 30 resultados. Refine a busca por nome ou código.</small>
              <small v-if="lookupErrors[field.lookup]" class="text-danger">{{ lookupErrors[field.lookup] }}
                <button type="button" class="btn btn-link btn-sm" @click="loadOptions(field.lookup)">Tentar novamente</button>
              </small>
              <small v-if="ticket && !form[field.id]" class="ast-muted">Registro anterior: {{ ticket[field.key] }}. Selecione o cadastro correspondente.</small>
            </template>
            <input v-else v-model="form[field.key]" class="form-control" :type="field.type || 'text'"
              :maxlength="field.max" :required="field.required" :list="field.list" autocomplete="off">
          </label>
          <label class="ast-field ast-full">Defeito relatado *
            <textarea v-model="form.defeito" class="form-control" required maxlength="1000" rows="3" />
          </label>
          <label class="ast-field">Prioridade
            <select v-model="form.prioridade" class="form-control">
              <option v-for="(label, key) in priorities" :key="key" :value="key">{{ label }}</option>
            </select>
          </label>
          <label class="ast-field">Orçamento (R$)
            <input v-model="form.valor" class="form-control" type="number" min="0" max="999999999.99" step="0.01" inputmode="decimal">
          </label>
          <label class="ast-full"><input v-model="form.garantia" type="checkbox"> Atendimento em garantia</label>
        </div>
        <datalist id="ast-tecnicos"><option v-for="tecnico in tecnicos" :key="tecnico">{{ tecnico }}</option></datalist>
        <div class="ast-actions">
          <button type="button" class="btn btn-outline-secondary" @click="$emit('close')">Cancelar</button>
          <button type="submit" class="btn btn-primary">{{ busy ? 'Salvando…' : ticket ? 'Salvar alterações' : 'Abrir chamado' }}</button>
        </div>
      </fieldset>
    </form>
  </b-modal>
</template>
<script>
import { BModal, BAlert } from 'bootstrap-vue';
import VSelect from 'vue-select';
import api from '@/api/AssistenciaTecnica/chamados.client';
import { fields, priorities, emptyTicket } from './options';

export default {
  components: { BModal, BAlert, VSelect },
  props: { visible: Boolean, ticket: { type: Object, default: null }, busy: Boolean, conflict: Boolean, error: { type: String, default: '' }, tecnicos: { type: Array, default: () => [] } },
  data: () => ({
    form: emptyTicket(), fields, priorities, selectionError: '',
    selected: { clientes: null, produtos: null }, options: { clientes: [], produtos: [] },
    loading: { clientes: false, produtos: false }, more: { clientes: false, produtos: false },
    lookupErrors: { clientes: '', produtos: '' }, requestIds: { clientes: 0, produtos: 0 }, timers: {},
  }),
  watch: {
    visible(value) { if (value) this.reset(); else this.cancelSearches(); },
    ticket() { if (this.visible) this.reset(); },
  },
  beforeDestroy() { this.cancelSearches(); },
  methods: {
    cancelSearches() {
      ['clientes', 'produtos'].forEach(kind => { clearTimeout(this.timers[kind]); this.requestIds[kind] += 1; });
    },
    searchOptions(kind, q) {
      clearTimeout(this.timers[kind]);
      this.requestIds[kind] += 1;
      this.timers[kind] = setTimeout(() => this.loadOptions(kind, q), 250);
    },
    async loadOptions(kind, q = '') {
      const requestId = ++this.requestIds[kind];
      this.loading[kind] = true;
      this.lookupErrors[kind] = '';
      try {
        const result = await api.lookup(kind, { q });
        if (requestId !== this.requestIds[kind]) return;
        this.options[kind] = result.options;
        this.more[kind] = result.hasMore;
      } catch (error) {
        if (requestId !== this.requestIds[kind]) return;
        this.options[kind] = [];
        this.lookupErrors[kind] = error.response?.data?.msg || error.response?.data?.message || 'Não foi possível carregar as opções.';
      } finally {
        if (requestId === this.requestIds[kind]) this.loading[kind] = false;
      }
    },
    choose(field, option) {
      this.selected[field.lookup] = option;
      this.form[field.id] = option ? option.id : '';
      this.form[field.key] = option ? option.nome : '';
      if (field.lookup === 'clientes' && option?.telefone) this.form.telefone = String(option.telefone).slice(0, 30);
      this.selectionError = '';
    },
    reset() {
      this.cancelSearches();
      this.selectionError = '';
      this.form = emptyTicket();
      if (this.ticket) Object.keys(this.form).forEach(key => { if (this.ticket[key] !== undefined) this.form[key] = this.ticket[key]; });
      fields.filter(field => field.lookup).forEach(field => {
        this.selected[field.lookup] = this.form[field.id]
          ? { id: this.form[field.id], nome: this.form[field.key], label: `${this.form[field.key]} (${this.form[field.id]})` } : null;
        this.options[field.lookup] = [];
        this.more[field.lookup] = false;
        this.loadOptions(field.lookup);
      });
    },
    submit() {
      if (!this.form.cliente_id || !this.form.produto_id) {
        this.selectionError = 'Selecione um cliente e um produto ativo da base da empresa.';
        return;
      }
      const payload = { ...this.form, valor: this.form.valor === '' || this.form.valor === null ? null : Number(this.form.valor) };
      if (this.ticket) payload.version = this.ticket.version;
      this.$emit('save', payload);
    },
  },
};
</script>